import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import {
  DynamoDBDocumentClient,
  ScanCommand,
  QueryCommand,
  PutCommand,
  DeleteCommand,
  UpdateCommand,
} from '@aws-sdk/lib-dynamodb';
import { toast } from 'sonner';
import {
  getWeekStart,
  getLastNCompleteWeeks,
  getCurrentWeekBoundaries,
} from './helpers';
import { detectEnvironment } from './featureFlags';

const CHAT_CONVERSATION_ENTITY = 'CHAT_CONVERSATION';

export type Citation = {
  confidence: number;
  retriever_type: string;
  knowledge_base: string;
  agent_source: string;
  text: string;
  title: string;
  uri: string;
};

export type Feedback = {
  rating: number; // 1 or 0, 1 for thumbs up, 0 for thumbs down
  comment: string;
};

export interface FlattenedConversation {
  pk: string;
  sk: string;
  session_id: string;
  user_id: string;
  entity_type?: string;
  question: string;
  answer: string;
  question_timestamp: string;
  answer_timestamp: string;
  formatted_question_time: string;
  formatted_answer_time: string;
  citations?: Citation[];
  title?: string;
  feedback?: Feedback;
}

export interface DynamodbConversationItem {
  pk: string;
  sk: string;
  date_time_string: string;
  title?: string;
  conversation: ConversationMessage[];
  entity_type?: string;
  user_id?: string;
}

export interface ConversationMessage {
  // message id assigned by the backend (UUID). Optional to allow older code paths.
  id?: string;
  role: string;
  content: any;
  timestamp: number | string;
  citations?: Citation[];
  // optional feedback the message
  feedback?: Feedback;
}

export interface ConversationSummary {
  pk: string;
  sk: string;
  session_id: string;
  user_id: string;
  title?: string;
  conversation: ConversationMessage[]; // The full conversation array from DynamoDB
  last_message_timestamp: string;
  formatted_last_message_time: string;
  message_count: number;
  ttl?: number; // TTL field if present
  entity_type?: string;
}

export interface ConversationFilters {
  user_id?: string;
  date_from?: string;
  date_to?: string;
  search_text?: string;
}

export interface DocumentStats {
  PK: string; // Partition key: DOCUMENT_STATS#{date}
  SK: string; // Sort key: DOC#{document_uri}
  entity_type: string; // 'DOCUMENT_STATS'
  document_uri: string; // URI of the cited document
  count: number; // number of times cited that day
  title?: string; // optional, from citation metadata
  agent_source?: string; // optional, from citation metadata
  last_updated: string; // ISO timestamp
  ttl?: number; // TTL for automatic cleanup
}

export interface GeneralFeedback {
  PK: string; // Always 'COMMENTS#GLOBAL'
  SK: string; // Timestamp#UserId format
  entity_type: string; // 'GLOBAL_FEEDBACK'
  user_id: string;
  comment: string;
  created_at: string; // ISO timestamp
  formatted_created_at?: string; // For display purposes
}

export interface FeedbackWeeklyStats {
  weekStart: string; // ISO date string for the start of the week
  positivePct: number; // Percentage of positive feedback (0-100)
  totalResponses: number; // Total number of feedback responses for that week
}

export interface CachedFeedbackStats {
  PK: string; // Cache partition key: FEEDBACK_STATS#{date}
  SK: string; // Sort key: WEEK#{weekStart}#{index}
  entity_type: string; // 'FEEDBACK_WEEKLY_STATS'
  week_start: string; // ISO date string for the start of the week
  positive_pct: number; // Percentage of positive feedback (0-100)
  total_responses: number; // Total number of feedback responses for that week
  computed_at: string; // ISO timestamp when stats were computed
}

class DynamoDBService {
  private client: DynamoDBDocumentClient | null = null;
  private tableName: string;

  constructor() {
    // Determine table name based on environment using consistent detection logic
    const environment = detectEnvironment();

    // Map environment to table name
    const tableMapping: Record<string, string> = {
      local: 'agile-conversations-local',
      cognito: 'agile-conversations-dev-v2',
      'dev-v2': 'agile-conversations-dev-v2',
      production: 'agile-conversations-production',
      'prod-v2': 'agile-conversations-prd-v2',
    };

    // default to dev-v2 if environment not recognized
    this.tableName = tableMapping[environment] || 'agile-conversations-dev-v2';

    console.log(
      `🗄️ DynamoDB Service: Environment=${environment}, Table=${this.tableName}`,
    );
  }

  async initializeClient(credentials: any) {
    if (!credentials) {
      throw new Error('AWS credentials required for DynamoDB access');
    }

    const dynamoClient = new DynamoDBClient({
      region: 'ap-southeast-2',
      credentials: credentials,
    });

    this.client = DynamoDBDocumentClient.from(dynamoClient);
  }

  private ensureClient() {
    if (!this.client) {
      throw new Error(
        'DynamoDB client not initialized. Call initializeClient first.',
      );
    }
    return this.client;
  }

  private extractTextFromContent(content: any): string {
    try {
      // Handle DocumentClient format: content[0].text
      if (Array.isArray(content) && content[0]?.text) {
        return content[0].text;
      }
      // Handle the complex DynamoDB structure: content.L[0].M.text.S
      if (content?.L?.[0]?.M?.text?.S) {
        return content.L[0].M.text.S;
      }
      // Fallback for simpler structures
      if (typeof content === 'string') {
        return content;
      }
      if (content?.text) {
        return content.text;
      }
      return 'No content';
    } catch (error) {
      console.error('Error extracting text from content:', error, content);
      return 'Error parsing content';
    }
  }

  private formatTimestamp(timestamp: any): {
    iso: string;
    formatted: string;
  } {
    try {
      let date: Date;

      if (typeof timestamp === 'number') {
        // DocumentClient format - direct number
        date = new Date(
          timestamp > 1000000000000 ? timestamp : timestamp * 1000,
        );
      } else if (timestamp?.N) {
        // DynamoDB number format - could be milliseconds or seconds
        const num = parseInt(timestamp.N);
        // If it's a very large number, it's likely milliseconds
        date = new Date(num > 1000000000000 ? num : num * 1000);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        date = new Date();
      }

      if (isNaN(date.getTime())) {
        const fallback = new Date().toISOString();
        return { iso: fallback, formatted: 'Invalid date' };
      }

      return {
        iso: date.toISOString(),
        formatted: date.toLocaleDateString() + ' ' + date.toLocaleTimeString(),
      };
    } catch (error) {
      const fallback = new Date().toISOString();
      console.error('Error formatting timestamp:', error);
      return { iso: fallback, formatted: 'Invalid date' };
    }
  }

  async getConversationSummaries(
    username: string,
  ): Promise<ConversationSummary[]> {
    const client = this.ensureClient();

    try {
      let allItems: any[] = [];

      if (username === '*') {
        console.info('Fetching conversation summaries for all users (admin)');
        // Use scan operation for all users (admin functionality)
        // Handle pagination to get ALL items
        let lastEvaluatedKey: any = undefined;
        let scanCount = 0;

        do {
          scanCount++;
          console.log(
            `Summary scan ${scanCount}${lastEvaluatedKey ? ' (continuation)' : ' (initial)'}`,
          );

          const command = new ScanCommand({
            TableName: this.tableName,
            ExclusiveStartKey: lastEvaluatedKey,
          });

          const response = await client.send(command);
          const batchItems = response.Items || [];
          allItems = allItems.concat(batchItems);

          lastEvaluatedKey = response.LastEvaluatedKey;
          if (lastEvaluatedKey) {
            console.log('More summary items available, continuing scan...');
          }
        } while (lastEvaluatedKey);
      } else {
        // Use query operation for specific user (much more efficient)
        if (!username) {
          throw new Error(
            'Username is required to query conversation summaries',
          );
        }

        // Handle pagination for query as well
        let lastEvaluatedKey: any = undefined;
        let queryCount = 0;

        do {
          queryCount++;
          console.log(
            `Summary query ${queryCount}${lastEvaluatedKey ? ' (continuation)' : ' (initial)'}`,
          );

          const command = new QueryCommand({
            TableName: this.tableName,
            KeyConditionExpression: 'PK = :userId',
            ExpressionAttributeValues: {
              ':userId': username,
            },
            ExclusiveStartKey: lastEvaluatedKey,
          });

          const response = await client.send(command);
          const batchItems = response.Items || [];
          allItems = allItems.concat(batchItems);

          lastEvaluatedKey = response.LastEvaluatedKey;
        } while (lastEvaluatedKey);
      }

      const items = allItems;
      const conversationSummaries: ConversationSummary[] = [];

      items.forEach((item, index) => {
        try {
          const entityType = (item.entity_type || item.entityType) as
            | string
            | undefined;
          if (entityType && entityType !== CHAT_CONVERSATION_ENTITY) {
            return;
          }
          // Extract session ID from SK (format: {session_id}#{agent_id})
          const sortKey = (item.SK || item.sk || '') as string;
          const skParts = sortKey.split('#');
          const sessionId = skParts[0] || 'unknown';
          const rawPk = (item.PK || item.pk || '') as string;
          const userId = rawPk || 'unknown';

          // Parse the conversation array to get message count and last timestamp
          const conversation = item.conversation || item.Conversation;
          let messages;

          if (Array.isArray(conversation)) {
            // DocumentClient format - direct array
            messages = conversation;
          } else if (conversation?.L) {
            // Raw DynamoDB format - has .L wrapper
            messages = conversation.L;
          } else {
            console.warn(
              `Skipping item ${index}: no valid conversation structure`,
            );
            return;
          }

          if (messages.length === 0) return;

          // Convert messages to typed format
          const typedMessages: ConversationMessage[] = messages.map(
            (msg: any) => {
              const messageObj = msg?.M || msg;
              return {
                role: messageObj?.role?.S || messageObj?.role || 'unknown',
                content: messageObj?.content || [],
                timestamp:
                  messageObj?.timestamp?.N || messageObj?.timestamp || 0,
                citations: messageObj?.citations || [],
              };
            },
          );

          // Get the last message timestamp
          const lastMessage = messages[messages.length - 1];
          const lastMessageObj = lastMessage?.M || lastMessage;
          const lastTimestamp = this.formatTimestamp(lastMessageObj.timestamp);

          // Generate title from first user message if not present
          let title = item.title;
          if (!title && typedMessages.length > 0) {
            const firstMessage = typedMessages[0];
            if (firstMessage.role === 'user') {
              const firstMessageText = this.extractTextFromContent(
                firstMessage.content,
              );
              // Trim to 100 characters for a reasonable title length
              title =
                firstMessageText.length > 100
                  ? firstMessageText.substring(0, 100).trim() + '...'
                  : firstMessageText.trim();
            }
          }

          const summary: ConversationSummary = {
            pk: rawPk,
            sk: sortKey,
            session_id: sessionId,
            user_id: userId,
            title,
            conversation: typedMessages, // Include the full typed conversation array
            last_message_timestamp: lastTimestamp.iso,
            formatted_last_message_time: lastTimestamp.formatted,
            message_count: messages.length,
            entity_type: entityType,
          };

          conversationSummaries.push(summary);
        } catch (error) {
          console.warn('Error parsing conversation summary:', error, item);
        }
      });

      // Sort by last message timestamp (most recent first)
      conversationSummaries.sort(
        (a, b) =>
          new Date(b.last_message_timestamp).getTime() -
          new Date(a.last_message_timestamp).getTime(),
      );

      return conversationSummaries;
    } catch (error) {
      console.error(
        `Error fetching conversation summaries (${username === '*' ? 'SCAN' : 'QUERY'}):`,
        error,
      );
      throw new Error('Failed to fetch conversation summaries from DynamoDB');
    }
  }

  /**
   * Get all conversations for a user, flattened into question-answer pairs.
   * @param username use * for all users (admin) or specific username
   * @param filters frontend filters only, data is fetched first then filtered. To be improved later with more advanced backend filtering if needed.
   * @param optimizeForRecent if true, adds date filtering to scan more recent items first
   * @returns
   */
  async getAllConversations(
    username: string,
    filters?: ConversationFilters,
    optimizeForRecent: boolean = false,
  ): Promise<FlattenedConversation[]> {
    const client = this.ensureClient();

    let response;

    try {
      let allItems: any[] = [];
      if (username === '*') {
        console.info('Fetching conversations for all users (admin)');
        // Use scan operation for all users (admin functionality)
        // Handle pagination to get ALL items
        let lastEvaluatedKey: any = undefined;
        let scanCount = 0;

        // Build filter expression based on optimization setting
        let filterExpression =
          'entity_type = :entityType OR attribute_not_exists(entity_type)';
        const expressionAttributeValues: any = {
          ':entityType': CHAT_CONVERSATION_ENTITY,
        };

        // If optimizing for recent items, add date filter for last 30 days
        if (optimizeForRecent) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const dateFilter = thirtyDaysAgo.toISOString().split('T')[0]; // YYYY-MM-DD format

          filterExpression +=
            ' AND (attribute_not_exists(date_time_string) OR date_time_string >= :dateFilter)';
          expressionAttributeValues[':dateFilter'] = dateFilter;

          console.log(`Optimizing scan for recent items (since ${dateFilter})`);
        }

        do {
          scanCount++;
          console.log(
            `Performing scan ${scanCount}${lastEvaluatedKey ? ' (continuation)' : ' (initial)'}`,
          );

          const command = new ScanCommand({
            TableName: this.tableName,
            FilterExpression: filterExpression,
            ExpressionAttributeValues: expressionAttributeValues,
            ExclusiveStartKey: lastEvaluatedKey,
          });

          response = await client.send(command);
          const batchItems = response.Items || [];
          allItems = allItems.concat(batchItems);

          lastEvaluatedKey = response.LastEvaluatedKey;
          if (lastEvaluatedKey) {
            console.log('More items available, continuing scan...');
          }
        } while (lastEvaluatedKey);

        console.log(
          `Completed scan. Total items retrieved: ${allItems.length}`,
        );
      } else {
        // Use query operation for specific user (much more efficient)
        console.info(`Fetching conversations for user: ${username}`);
        if (!username) {
          throw new Error('Username is required to query conversations');
        }

        // Handle pagination for query as well
        let lastEvaluatedKey: any = undefined;
        let queryCount = 0;

        do {
          queryCount++;
          console.log(
            `Performing query ${queryCount}${lastEvaluatedKey ? ' (continuation)' : ' (initial)'}`,
          );

          const command = new QueryCommand({
            TableName: this.tableName,
            KeyConditionExpression: 'PK = :userId',
            ExpressionAttributeValues: {
              ':userId': username,
            },
            ExclusiveStartKey: lastEvaluatedKey,
          });

          response = await client.send(command);
          const batchItems = response.Items || [];
          allItems = allItems.concat(batchItems);
          lastEvaluatedKey = response.LastEvaluatedKey;
        } while (lastEvaluatedKey);
      }

      const items = allItems as any[]; // Use any[] to handle both DocumentClient and raw DynamoDB formats

      const flattenedConversations: FlattenedConversation[] = [];

      items.forEach((item, index) => {
        try {
          const entityType = (item.entity_type || item.entityType) as
            | string
            | undefined;

          // Extract session ID from SK (format: {session_id}#{agent_id})
          const sortKey = (item.SK || item.sk || '') as string;
          const skParts = sortKey.split('#');
          const sessionId = skParts[0] || 'unknown';
          const rawPk = (item.PK || item.pk || '') as string;
          const userId = rawPk || 'unknown';

          // Debug: Log item processing
          // console.log(`Processing item ${index}:`, { sessionId, userId });

          // Parse the conversation array
          const conversation = item.conversation || item.Conversation;

          // Handle DocumentClient format (simplified) vs raw DynamoDB format
          let messages: ConversationMessage[];
          if (Array.isArray(conversation)) {
            // DocumentClient format - direct array
            messages = conversation;
          } else if (conversation?.L) {
            // Raw DynamoDB format - has .L wrapper
            messages = conversation.L;
          } else {
            console.warn(
              `Skipping item ${index}: no valid conversation structure`,
            );
            return;
          }

          // Group messages into question-answer pairs
          for (let i = 0; i < messages.length - 1; i += 2) {
            // Handle both DocumentClient format (direct object) and raw DynamoDB format (.M wrapper)
            const userMessage = messages[i] || messages[i];
            const assistantMessage = messages[i + 1] || messages[i + 1];

            // Handle both DocumentClient format (.role) and raw DynamoDB format (.role.S)
            const userRole = userMessage?.role;
            const assistantRole = assistantMessage?.role;

            // Debug: Log message pair processing
            // console.log(`Processing message pair ${i}/${i+1}:`, { userRole, assistantRole });

            if (userRole === 'user' && assistantRole === 'assistant') {
              const questionText = this.extractTextFromContent(
                userMessage.content,
              );
              const answerText = this.extractTextFromContent(
                assistantMessage.content,
              );

              // Debug: Log extracted texts
              // console.log('Extracted texts:', { questionText, answerText });

              const questionTime = this.formatTimestamp(userMessage.timestamp);
              const answerTime = this.formatTimestamp(
                assistantMessage.timestamp,
              );

              // Generate title from first user message if not present
              // Only set title for the first Q&A pair (i === 0)
              let title = item.title;
              if (!title && i === 0) {
                // Trim to 100 characters for a reasonable title length
                title =
                  questionText.length > 100
                    ? questionText.substring(0, 100).trim() + '...'
                    : questionText.trim();
              }

              const flattenedConv: FlattenedConversation = {
                pk: rawPk,
                sk: sortKey,
                session_id: sessionId,
                user_id: userId,
                entity_type: entityType,
                question: questionText,
                answer: answerText,
                question_timestamp: questionTime.iso,
                answer_timestamp: answerTime.iso,
                formatted_question_time: questionTime.formatted,
                formatted_answer_time: answerTime.formatted,
                citations: assistantMessage.citations || [],
                title,
                feedback: assistantMessage.feedback,
              };

              // Debug: Log flattened conversation
              // console.log('Adding flattened conversation:', flattenedConv);
              flattenedConversations.push(flattenedConv);
            }
          }
        } catch (error) {
          console.warn('Error parsing conversation item:', error, item);
        }
      });

      // Apply filters
      let filteredConversations = flattenedConversations;

      console.log(flattenedConversations);

      // Note: When using query operation with specific username,
      // conversations are already filtered by user_id, so no need to filter again
      // Only apply user_id filter when using scan operation (username === '*')
      if (username === '*' && filters?.user_id) {
        filteredConversations = filteredConversations.filter((conv) =>
          conv.user_id.toLowerCase().includes(filters.user_id!.toLowerCase()),
        );
      }

      if (filters?.search_text) {
        const searchLower = filters.search_text.toLowerCase();
        filteredConversations = filteredConversations.filter(
          (conv) =>
            conv.question.toLowerCase().includes(searchLower) ||
            conv.answer.toLowerCase().includes(searchLower),
        );
      }

      if (filters?.date_from) {
        const fromDate = new Date(filters.date_from);
        filteredConversations = filteredConversations.filter(
          (conv) => new Date(conv.question_timestamp) >= fromDate,
        );
      }

      if (filters?.date_to) {
        const toDate = new Date(filters.date_to + 'T23:59:59');
        filteredConversations = filteredConversations.filter(
          (conv) => new Date(conv.question_timestamp) <= toDate,
        );
      }

      // Sort by question timestamp (most recent first)
      filteredConversations.sort(
        (a, b) =>
          new Date(b.question_timestamp).getTime() -
          new Date(a.question_timestamp).getTime(),
      );

      return filteredConversations;
    } catch (error) {
      console.error(
        `Error fetching conversations (${username === '*' ? 'SCAN' : 'QUERY'}):`,
        error,
      );
      throw new Error('Failed to fetch conversations from DynamoDB');
    }
  }

  async getDocumentStats(): Promise<DocumentStats[]> {
    const client = this.ensureClient();
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    // First, check if stats for the given date already exist in the conversations table
    try {
      const queryCommand = new QueryCommand({
        TableName: this.tableName,
        KeyConditionExpression: 'PK = :pk',
        FilterExpression: 'entity_type = :entityType',
        ExpressionAttributeValues: {
          ':pk': `DOCUMENT_STATS#${today}`,
          ':entityType': 'DOCUMENT_STATS',
        },
      });

      const queryResponse = await client.send(queryCommand);
      const existingItems = queryResponse.Items || [];

      if (existingItems.length > 0) {
        // Convert back to the old format for backward compatibility
        console.info(
          `Document stats for ${today} already exist, reusing ${existingItems.length} items.`,
        );
        return existingItems.map((item: any) => ({
          PK: item.PK,
          SK: item.SK,
          entity_type: item.entity_type,
          document_uri: item.document_uri,
          count: item.count,
          title: item.title,
          agent_source: item.agent_source,
          last_updated: item.last_updated,
          ttl: item.ttl,
        }));
      }
    } catch (error) {
      console.error(
        'Error querying document stats from conversations table:',
        error,
      );
      // Proceed to fallback calculation
    }

    console.info(
      `Calculating document stats for ${today} from conversations...`,
    );
    let conversations: FlattenedConversation[] = [];
    try {
      // For document stats, we might want to get all conversations from all users
      // This is a special case where we pass '*' to indicate all users
      conversations = await this.getAllConversations('*');
    } catch (error) {
      console.error('Error fetching conversations for document stats:', error);
      throw new Error('Failed to fetch conversations for document stats');
    }

    const statsMap: Record<string, any> = {};

    conversations.forEach((conv) => {
      const citations = conv.citations || [];
      citations.forEach((citation: Citation) => {
        if (!citation.uri) return;
        const key = citation.uri;
        if (!statsMap[key]) {
          statsMap[key] = {
            PK: `DOCUMENT_STATS#${today}`,
            SK: `DOC#${key}`,
            entity_type: 'DOCUMENT_STATS',
            document_uri: key,
            count: 0,
            title: citation.title,
            agent_source: citation.agent_source,
            last_updated: new Date().toISOString(),
            ttl: Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60, // Expire in 7 days
          };
        }
        statsMap[key].count += 1;
        statsMap[key].last_updated = new Date().toISOString();
      });
    });

    const statsArray = Object.values(statsMap);

    // Save each item into the conversations table
    for (const stat of statsArray) {
      try {
        const putCommand = new PutCommand({
          TableName: this.tableName,
          Item: stat,
        });
        await client.send(putCommand);
      } catch (error) {
        console.error('Error saving document stat:', stat, error);
      }
    }

    return statsArray;
  }

  getTableName(): string {
    return this.tableName;
  }

  getEnvironment(): string {
    return detectEnvironment();
  }

  // Utility function to get the first message content for preview
  getConversationPreview(conversation: ConversationMessage[]): string {
    const userMessages = conversation.filter((msg) => msg.role === 'user');
    if (userMessages.length === 0) return 'No messages';

    const firstUserMessage = userMessages[0];
    const content = this.extractTextFromContent(firstUserMessage.content);

    // Truncate if too long
    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  }

  async updateConversationTitle(
    pk: string,
    sk: string,
    newTitle: string,
  ): Promise<void> {
    if (!this.client) {
      throw new Error('DynamoDB client not initialized');
    }

    try {
      const command = new UpdateCommand({
        TableName: this.tableName,
        Key: {
          PK: pk,
          SK: sk,
        },
        UpdateExpression: 'SET title = :title',
        ExpressionAttributeValues: {
          ':title': newTitle,
        },
      });

      await this.client.send(command);
    } catch (error) {
      console.error('Failed to update conversation title:', error);
      throw error;
    }
  }

  /**
   * Save feedback for a specific message in a conversation.
   * Attempts to locate the message by index for the given user (PK). If `sk` is provided
   * the search is scoped to that conversation item, otherwise all conversation items for the
   * user are scanned until the message is located.
   *
   * Note: For robustness we fetch the full item, update the conversation array in-memory,
   * and write the item back with PutCommand. This handles both DocumentClient and simple
   * list formats in the UI. If your dataset is large consider a more targeted update.
   */
  async saveMessageFeedback(
    pk: string,
    sessionId: string,
    feedback: Feedback,
    messageIndex?: number,
  ): Promise<void> {
    const sk = `${sessionId}#`;
    if (!this.client) {
      throw new Error('DynamoDB client not initialized');
    }
    const client = this.client;

    if (messageIndex === undefined || messageIndex === null) {
      console.warn('Cannot persist feedback without a message index');
      toast.warning('Could not save feedback: missing message index', {
        duration: 5000,
      });
      return;
    }
    const targetIndex = Number(messageIndex);

    if (Number.isNaN(targetIndex) || targetIndex < 0) {
      console.warn(
        'Invalid message index supplied for feedback:',
        messageIndex,
      );
      toast.warning('Could not save feedback: invalid message reference', {
        duration: 5000,
      });
      return;
    }

    try {
      if (!pk) {
        console.warn('Cannot persist feedback without a partition key');
        toast.warning('Could not save feedback: missing user identifier', {
          duration: 5000,
        });
        return;
      }

      const command = new QueryCommand({
        TableName: this.tableName,
        KeyConditionExpression: 'PK = :pk AND begins_with(SK, :sk)',
        ExpressionAttributeValues: {
          ':pk': pk,
          ':sk': sk,
        },
      });

      const response = await client.send(command);
      const items = response.Items || [];

      if (!items || items.length === 0) {
        throw new Error('No conversation items found for user');
      }

      // Try to find the message in the conversation arrays
      let found = false;
      for (const item of items) {
        const entityType = item.entity_type || item.entityType;
        if (entityType && entityType !== CHAT_CONVERSATION_ENTITY) {
          continue;
        }
        let conversation = item.conversation;

        // Normalize raw DynamoDB format (.L/.M) -> plain array of objects
        if (!Array.isArray(conversation) && conversation?.L) {
          conversation = conversation.L.map((entry: any) => entry.M || entry);
        }

        if (!Array.isArray(conversation)) continue;
        const integerIndex = Math.trunc(targetIndex);
        if (integerIndex < 0 || integerIndex >= conversation.length) {
          continue;
        }

        const msg = conversation[integerIndex];
        if (!msg) {
          continue;
        }

        conversation[integerIndex] = {
          ...msg,
          feedback,
        };

        const updatedItem = { ...item, conversation };
        try {
          const put = new PutCommand({
            TableName: this.tableName,
            Item: updatedItem,
          });
          await client.send(put);
          found = true;
          break;
        } catch (err) {
          console.error('Failed to write updated conversation item:', err);
          throw err;
        }
      }

      if (!found) {
        // Not treating as fatal — caller can decide how to surface this
        toast.warning('Could not find message to feedback', {
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Error saving message feedback:', error);
      throw error;
    }
  }

  async saveGeneralFeedback(userId: string, comment: string): Promise<void> {
    const client = this.ensureClient();

    const timestamp = new Date().toISOString();
    const item = {
      PK: 'COMMENTS#GLOBAL',
      SK: `${timestamp}#${userId}`,
      entity_type: 'GLOBAL_FEEDBACK',
      user_id: userId,
      comment,
      created_at: timestamp,
    };

    try {
      const command = new PutCommand({
        TableName: this.tableName,
        Item: item,
      });
      await client.send(command);
    } catch (error) {
      console.error('Failed to save global feedback:', error);
      throw error;
    }
  }

  async deleteConversation(pk: string, sk: string): Promise<void> {
    if (!this.client) {
      throw new Error('DynamoDB client not initialized');
    }

    try {
      const command = new DeleteCommand({
        TableName: this.tableName,
        Key: {
          PK: pk,
          SK: sk,
        },
      });

      await this.client.send(command);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw error;
    }
  }

  async getGeneralFeedback(): Promise<GeneralFeedback[]> {
    const client = this.ensureClient();

    try {
      const command = new QueryCommand({
        TableName: this.tableName,
        KeyConditionExpression: 'PK = :pk',
        ExpressionAttributeValues: {
          ':pk': 'COMMENTS#GLOBAL',
        },
        ScanIndexForward: false, // Sort by SK in descending order (newest first)
      });

      const response = await client.send(command);
      const items = response.Items || [];

      // Transform and format the data
      const feedbackItems: GeneralFeedback[] = items.map((item: any) => {
        const formatted = this.formatTimestamp(item.created_at);
        return {
          PK: item.PK,
          SK: item.SK,
          entity_type: item.entity_type,
          user_id: item.user_id,
          comment: item.comment,
          created_at: item.created_at,
          formatted_created_at: formatted.formatted,
        };
      });

      return feedbackItems;
    } catch (error) {
      console.error('Error fetching global feedback:', error);
      throw new Error('Failed to fetch global feedback from DynamoDB');
    }
  }

  async getFeedbackWeeklyStats(
    refresh: boolean = false,
  ): Promise<FeedbackWeeklyStats[]> {
    const client = this.ensureClient();

    try {
      // Check if we have cached stats for today
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const cacheKey = `FEEDBACK_STATS#${today}`;

      // If refresh is false, try to fetch cached stats first
      if (!refresh) {
        try {
          const cacheQuery = new QueryCommand({
            TableName: this.tableName,
            KeyConditionExpression: 'PK = :pk',
            ExpressionAttributeValues: {
              ':pk': cacheKey,
            },
          });

          const cacheResponse = await client.send(cacheQuery);
          if (cacheResponse.Items && cacheResponse.Items.length > 0) {
            // Validate that cached data has proper Monday-based week starts
            const cachedStats = cacheResponse.Items.map((item: any) => ({
              weekStart: item.week_start,
              positivePct: item.positive_pct,
              totalResponses: item.total_responses,
            }));

            // Check if all week starts are actually Mondays
            const allMondays = cachedStats.every((stat) => {
              const date = new Date(stat.weekStart);
              return date.getUTCDay() === 1; // Monday = 1
            });

            if (allMondays) {
              // Return cached stats, sorted by weekStart
              return cachedStats.sort((a, b) =>
                a.weekStart.localeCompare(b.weekStart),
              );
            }
          }
        } catch (cacheError) {
          console.warn('Cache fetch failed:', cacheError);
        }
      }

      // Get all conversations from the last 5 weeks (4 complete weeks + current incomplete week)
      // Use UTC consistently since DynamoDB timestamps are stored in UTC
      const currentDate = new Date();
      const weekStartRef = new Date(currentDate);
      const currentDay = weekStartRef.getUTCDay();
      const daysToSubtract = currentDay === 0 ? 6 : currentDay - 1; // Get to Monday
      weekStartRef.setUTCDate(currentDate.getUTCDate() - daysToSubtract);
      weekStartRef.setUTCHours(0, 0, 0, 0);

      const fiveWeeksAgo = new Date(weekStartRef);
      fiveWeeksAgo.setUTCDate(weekStartRef.getUTCDate() - 5 * 7); // 5 weeks back

      // Query all conversation items from all users for feedback analysis
      const scanCommand = new ScanCommand({
        TableName: this.tableName,
        FilterExpression:
          'entity_type = :entityType AND date_time_string >= :dateFilter',
        ExpressionAttributeValues: {
          ':entityType': CHAT_CONVERSATION_ENTITY,
          ':dateFilter': fiveWeeksAgo.toISOString().split('T')[0], // YYYY-MM-DD format
        },
      });

      // Handle pagination to get ALL matching items
      let allItems: any[] = [];
      let lastEvaluatedKey: any = undefined;

      do {
        const paginatedCommand = {
          ...scanCommand.input,
          ...(lastEvaluatedKey && { ExclusiveStartKey: lastEvaluatedKey }),
        };

        const response = await client.send(new ScanCommand(paginatedCommand));
        const batchItems = response.Items || [];
        allItems = allItems.concat(batchItems);

        lastEvaluatedKey = response.LastEvaluatedKey;
      } while (lastEvaluatedKey);

      const items = allItems;

      // Process conversations to extract feedback data by week
      const weeklyFeedbackMap: Record<
        string,
        { positive: number; total: number }
      > = {};

      items.forEach((item) => {
        try {
          const conversation = item.conversation || [];
          let messages: any[];

          if (Array.isArray(conversation)) {
            messages = conversation;
          } else if (conversation?.L) {
            messages = conversation.L.map((entry: any) => entry.M || entry);
          } else {
            return;
          }

          // Look for messages with feedback in the last 5 weeks
          messages.forEach((msg: any) => {
            const feedback = msg.feedback;
            if (!feedback || typeof feedback.rating !== 'number') {
              return;
            }

            // Get message timestamp
            let messageDate: Date;
            const timestamp = msg.timestamp;

            if (typeof timestamp === 'number') {
              messageDate = new Date(
                timestamp > 1000000000000 ? timestamp : timestamp * 1000,
              );
            } else if (timestamp?.N) {
              const num = parseInt(timestamp.N);
              messageDate = new Date(num > 1000000000000 ? num : num * 1000);
            } else if (typeof timestamp === 'string') {
              messageDate = new Date(timestamp);
            } else {
              return;
            }

            // Skip if message is older than 5 weeks
            if (messageDate < fiveWeeksAgo) {
              return;
            }

            const weekStart = getWeekStart(messageDate);

            if (!weeklyFeedbackMap[weekStart]) {
              weeklyFeedbackMap[weekStart] = { positive: 0, total: 0 };
            }

            weeklyFeedbackMap[weekStart].total += 1;
            if (feedback.rating === 1) {
              // 1 = positive (thumbs up)
              weeklyFeedbackMap[weekStart].positive += 1;
            }
          });
        } catch (error) {
          console.warn('Error processing conversation:', error);
        }
      });

      // Generate stats for the last 5 weeks (4 complete weeks + current incomplete week)
      const stats: FeedbackWeeklyStats[] = [];
      const completeWeeks = getLastNCompleteWeeks(4); // Get 4 complete weeks
      const currentWeekBoundaries = getCurrentWeekBoundaries();

      completeWeeks.forEach((weekStart) => {
        const weekData = weeklyFeedbackMap[weekStart] || {
          positive: 0,
          total: 0,
        };
        const positivePct =
          weekData.total > 0
            ? Math.round((weekData.positive / weekData.total) * 100)
            : 0;

        stats.push({
          weekStart,
          positivePct,
          totalResponses: weekData.total,
        });
      });

      // Add the current incomplete week
      const currentWeekStartDate = currentWeekBoundaries.start;
      const currentWeekData = weeklyFeedbackMap[currentWeekStartDate] || {
        positive: 0,
        total: 0,
      };
      const currentWeekPositivePct =
        currentWeekData.total > 0
          ? Math.round((currentWeekData.positive / currentWeekData.total) * 100)
          : 0;

      stats.push({
        weekStart: currentWeekStartDate,
        positivePct: currentWeekPositivePct,
        totalResponses: currentWeekData.total,
      });

      // Sort by weekStart to ensure chronological order
      stats.sort((a, b) => a.weekStart.localeCompare(b.weekStart));

      // Cache the computed stats for today
      const cacheTimestamp = new Date().toISOString();

      const cachePromises = stats.map((stat, index) => {
        const cacheItem = {
          PK: cacheKey,
          SK: `WEEK#${stat.weekStart}#${index.toString().padStart(2, '0')}`,
          entity_type: 'FEEDBACK_WEEKLY_STATS',
          week_start: stat.weekStart,
          positive_pct: stat.positivePct,
          total_responses: stat.totalResponses,
          computed_at: cacheTimestamp,
          ttl: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // Expire in 24 hours
        };

        return client.send(
          new PutCommand({
            TableName: this.tableName,
            Item: cacheItem,
          }),
        );
      });

      // Save cache entries (don't wait for completion, fire and forget)
      Promise.all(cachePromises).catch((error) => {
        console.warn('Failed to cache stats:', error);
      });

      return stats;
    } catch (error) {
      console.error('Error fetching feedback weekly stats:', error);
      throw new Error('Failed to fetch feedback weekly stats from DynamoDB');
    }
  }

  /**
   * Clear cached feedback weekly stats for a specific date or all dates
   * @param date Optional date string (YYYY-MM-DD). If not provided, clears all cached stats
   */
  async clearCachedFeedbackStats(date?: string): Promise<void> {
    const client = this.ensureClient();

    try {
      if (date) {
        // Clear cache for specific date
        const cacheKey = `FEEDBACK_STATS#${date}`;
        const queryCommand = new QueryCommand({
          TableName: this.tableName,
          KeyConditionExpression: 'PK = :pk',
          ExpressionAttributeValues: {
            ':pk': cacheKey,
          },
        });

        const response = await client.send(queryCommand);
        const items = response.Items || [];

        const deletePromises = items.map((item) =>
          client.send(
            new DeleteCommand({
              TableName: this.tableName,
              Key: {
                PK: item.PK,
                SK: item.SK,
              },
            }),
          ),
        );

        await Promise.all(deletePromises);
        console.log(`Cleared cached feedback stats for date: ${date}`);
      } else {
        // Clear all cached stats using scan
        const scanCommand = new ScanCommand({
          TableName: this.tableName,
          FilterExpression: 'entity_type = :entityType',
          ExpressionAttributeValues: {
            ':entityType': 'FEEDBACK_WEEKLY_STATS',
          },
        });

        const response = await client.send(scanCommand);
        const items = response.Items || [];

        const deletePromises = items.map((item) =>
          client.send(
            new DeleteCommand({
              TableName: this.tableName,
              Key: {
                PK: item.PK,
                SK: item.SK,
              },
            }),
          ),
        );

        await Promise.all(deletePromises);
        console.log('Cleared all cached feedback stats');
      }
    } catch (error) {
      console.error('Error clearing cached feedback stats:', error);
      throw new Error('Failed to clear cached feedback stats');
    }
  }
}

export const dynamoDBService = new DynamoDBService();
